import {ListViewSectionFilterLink} from '@github-ui/list-view/ListViewSectionFilterLink'
import {useParams} from '@github-ui/react-router'
import type {RelayCompat} from '@github-ui/server-persisted-queries/relay-compat'
import {useRuntimeEnvironment} from '@github-ui/ssr-utils/use-runtime-environment'
import {useIsPlatform} from '@github-ui/use-is-platform'
import {useNavigate, useSearchParams} from '@github-ui/use-navigate'
import {clsx} from 'clsx'
import {useCallback, useMemo} from 'react'

import type {RepositoryResult} from './repository-milestone-index-page-query-types'
import styles from './RepositoryMilestone.module.css'

// RELAY-CLEANUP(RepositoryMilestonesInternal): widens nullable fields so the still-Relay parent
// data is assignable. Remove when RepositoryMilestonesInternal leaves Relay.
export type OpenClosedMilestonesFragment = RelayCompat<Pick<RepositoryResult, 'open' | 'closed'>>

export function OpenClosedMilestones({repository}: {repository: OpenClosedMilestonesFragment}) {
  const openIssueCount = Math.abs(repository?.open?.totalCount || 0).toLocaleString()
  const closedIssueCount = Math.abs(repository?.closed?.totalCount || 0).toLocaleString()

  // Keep base path stable to avoid hydration errors.
  const {owner = '', repo = ''} = useParams<{owner: string; repo: string}>()
  const basePath = useMemo(() => `/${owner}/${repo}/milestones`, [owner, repo])
  const openHrefUrl = basePath
  const closedHrefUrl = `${basePath}?state=closed`
  const [searchParams] = useSearchParams()
  const searchString = searchParams.get('state')
  const isMac = useIsPlatform(['mac'])
  const navigate = useNavigate()
  const onClick = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>, isOpen: boolean) => {
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      const isMetaKey = isMac ? event.metaKey : event.ctrlKey
      if (isMetaKey) {
        return
      }

      event.preventDefault()
      const params = new URLSearchParams(searchParams)
      // Reset pagination when the filter changes so a stale ?page= can't strand the
      // user on an empty out-of-range page of the newly selected filter.
      params.delete('page')
      if (isOpen) {
        params.delete('state')
      } else {
        params.set('state', 'closed')
      }
      const paramString = params.toString()
      const href = paramString ? `${basePath}?${paramString}` : basePath

      navigate(href, {preventAutofocus: true})
    },
    [basePath, isMac, navigate, searchParams],
  )

  const {isBrowser} = useRuntimeEnvironment()
  return (
    <div>
      <ul className={`list-style-none ${clsx(styles.tabsContainer)}`}>
        <li key={`section-filter-0`}>
          <ListViewSectionFilterLink
            key="open"
            title="Open"
            isSelected={isBrowser && searchString !== 'closed'}
            count={openIssueCount}
            href={openHrefUrl}
            onClick={e => onClick(e, true)}
            data-testid="open-milestone-tab"
          />
        </li>
        <li key={`section-filter-1`}>
          <ListViewSectionFilterLink
            key="closed"
            title="Closed"
            isSelected={isBrowser && searchString === 'closed'}
            count={closedIssueCount}
            href={closedHrefUrl}
            onClick={e => onClick(e, false)}
            data-testid="closed-milestone-tab"
          />
        </li>
      </ul>
    </div>
  )
}
